import { motion } from "framer-motion";
import { Package, Users, TruckTrailer, Barcode, ClipboardText, ChartBar } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { Reveal, Stagger, StaggerItem, LineReveal } from "../components/motion";
import { StatusBadge, Placeholder } from "../components/StatusBadge";

const modules = [
  { i: Barcode, t: "Inventory Control", d: "Real-time stock, bin and batch accuracy across the facility.", s: "Live" },
  { i: Users, t: "Labour Management", d: "Plan, allocate and measure workforce against demand.", s: "Live" },
  { i: TruckTrailer, t: "Dispatch & Outbound", d: "Orchestrate picking, staging and dispatch with SLA control.", s: "Live" },
  { i: Package, t: "Inbound & Putaway", d: "Structured receiving into rack and bin locations.", s: "Live" },
  { i: ClipboardText, t: "Factory Flow", d: "Connect production output to warehouse and dispatch.", s: "In deployment" },
  { i: ChartBar, t: "Operations Analytics", d: "Floor-true metrics feeding EazyOS — 100% operational visibility.", s: "In deployment" },
];

export default function EazyMS() {
  return (
    <Layout>
      <SEO title="EazyMS — Warehouse & Factory Management" description="EazyMS manages inventory, labour and dispatch as one operating system. The warehouse & factory product inside the Eazymuv OS ecosystem." />
      {/* Branded hero: EazyMS yellow + black */}
      <section data-testid="eazyms-hero" className="relative bg-ms-yellow text-ez-text overflow-hidden border-b border-ez-text">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute top-0 left-0 right-0 h-2 hazard" />
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-8 py-24 md:py-32">
          <div className="font-mono-ez text-xs uppercase tracking-[0.3em] mb-6">A product of Eazymuv OS · Warehouse & Factory</div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-[10vw] uppercase tracking-tighter leading-[0.85]">
            <LineReveal lines={["EazyMS"]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 max-w-2xl text-lg font-medium">
            Manage inventory, labour and dispatch as one operating system — the product
            layer that turns managed operations into floor-true data.
          </motion.p>
          <div className="mt-8 flex items-center gap-3">
            <StatusBadge status="Live" />
            <Placeholder label="Product mock-up" />
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-ez-text">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter max-w-3xl leading-tight">One system for the whole facility</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-ez-text">
            {modules.map((m, i) => (
              <StaggerItem key={i} className="group border-b border-r border-ez-border p-8 hover:bg-ms-yellow transition-colors">
                <div className="flex justify-between items-start mb-6">
                  <m.i size={30} weight="duotone" className="text-ez-text" />
                  <StatusBadge status={m.s} />
                </div>
                <div className="font-display text-xl uppercase tracking-tight leading-tight">{m.t}</div>
                <p className="mt-3 text-sm text-ez-text/70">{m.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-ez-bg">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <div className="font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-text/50 mb-4">Get started</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter mb-10">Request a demo, integration or deployment</h2>
          </Reveal>
          <Reveal delay={0.1}><LeadForm variant="eazyms" theme="ms" /></Reveal>
        </div>
      </section>
    </Layout>
  );
}
